import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

const PREORDER_TYPE_PROPERTY = 'نوع الطلب';
const PREORDER_TYPE_VALUE = 'طلب مسبق';
const PREORDER_ETA_PROPERTY = 'تاريخ التوفر المتوقع';
const PREORDER_POLICY_PROPERTY = 'سياسة التأخير';
const DEFAULT_DELAY_POLICY = 'قد يتأخر الشحن حسب توفر المنتج';
const INVENTORY_POLICY_DENY_MESSAGE = 'لا يمكن تحويل الزيادة إلى طلب مسبق لأن إعدادات المخزون لا تسمح بالبيع عند نفاد الكمية.';
const CHECKOUT_GUARD_MESSAGE = 'تعذر تحديث بعض كميات الطلب المسبق تلقائيًا. الرجاء تحديث السلة والمحاولة مرة أخرى.';
const CHECKOUT_PREPARING_MESSAGE = 'جاري تقسيم الكميات بين المتوفر والطلب المسبق...';

const toInt = (value, fallback = 0) => {
  const parsed = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};
const isTrue = (value) => value === true || value === 'true';

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #checkoutPreparationInProgress = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener('click', this.#handleCheckoutAttempt, true);
    document.addEventListener('submit', this.#handleCartFormSubmit, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener('click', this.#handleCheckoutAttempt, true);
    document.removeEventListener('submit', this.#handleCartFormSubmit, true);
  }

  #handleCheckoutAttempt = (event) => {
    if (!(event.target instanceof Element)) return;
    const checkoutTrigger = event.target.closest('#checkout, .cart__checkout-button, [name="checkout"], .additional-checkout-buttons, shopify-accelerated-checkout-cart');
    if (!checkoutTrigger || this.#isCartReadyForCheckout()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#prepareCartForCheckout();
  };

  #handleCartFormSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form' || this.#isCartReadyForCheckout()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#prepareCartForCheckout();
  };

  async #prepareCartForCheckout() {
    if (this.#checkoutPreparationInProgress) return;
    this.#checkoutPreparationInProgress = true;
    this.#showCheckoutGuardMessage(CHECKOUT_PREPARING_MESSAGE);
    try {
      for (let attempt = 0; attempt < 12; attempt += 1) {
        const pendingSplit = this.#getNextPendingSplit();
        if (!pendingSplit) {
          this.#hideCheckoutGuardMessage();
          window.location.assign('/checkout');
          return;
        }
        if (pendingSplit.inventoryPolicy !== 'continue' && !pendingSplit.bundleHasPreorder) {
          this.#showCheckoutGuardMessage(INVENTORY_POLICY_DENY_MESSAGE);
          return;
        }
        const updated = await this.updateQuantity({
          line: pendingSplit.line,
          quantity: pendingSplit.normalQuantity,
          action: 'change',
          preorderQuantity: pendingSplit.preorderQuantity,
          preorderProperties: {
            eta: pendingSplit.eta,
            delayPolicy: pendingSplit.delayPolicy,
            variantId: pendingSplit.variantId,
          },
        });
        if (!updated) {
          this.#showCheckoutGuardMessage(CHECKOUT_GUARD_MESSAGE);
          return;
        }
        await new Promise((resolve) => window.requestAnimationFrame(resolve));
      }
      if (this.#isCartReadyForCheckout()) {
        this.#hideCheckoutGuardMessage();
        window.location.assign('/checkout');
      } else {
        this.#showCheckoutGuardMessage(CHECKOUT_GUARD_MESSAGE);
      }
    } finally {
      this.#checkoutPreparationInProgress = false;
    }
  }

  #getNextPendingSplit() {
    const rows = Array.from(document.querySelectorAll('.cart-items__table-row'));
    const remainingInventoryByVariant = new Map();
    for (const row of rows) {
      const selector = row.querySelector('cart-quantity-selector-component');
      const input = selector?.querySelector('input[ref="quantityInput"], input[name="updates[]"]');
      if (!(selector instanceof HTMLElement) || !(input instanceof HTMLInputElement)) continue;
      const splitConfig = this.#getCartSplitConfig(row, selector);
      if (!splitConfig.shouldSplit || !splitConfig.variantId) continue;
      const availableInventory = remainingInventoryByVariant.has(splitConfig.variantId)
        ? remainingInventoryByVariant.get(splitConfig.variantId)
        : Math.max(splitConfig.availableInventory, 0);
      const requestedQuantity = Math.max(toInt(input.value, 0), 0);
      const normalQuantity = Math.min(requestedQuantity, availableInventory);
      const preorderQuantity = requestedQuantity - normalQuantity;
      remainingInventoryByVariant.set(splitConfig.variantId, Math.max(availableInventory - normalQuantity, 0));
      if (preorderQuantity <= 0) continue;
      const line = toInt(input.dataset.cartLine, 0);
      if (line <= 0) continue;
      return {
        line,
        normalQuantity,
        preorderQuantity,
        variantId: splitConfig.variantId,
        inventoryPolicy: splitConfig.inventoryPolicy,
        eta: splitConfig.eta,
        delayPolicy: splitConfig.delayPolicy,
        bundleHasPreorder: splitConfig.bundleHasPreorder,
      };
    }
    return null;
  }

  #isCartReadyForCheckout() {
    const rows = Array.from(document.querySelectorAll('.cart-items__table-row'));
    if (!rows.length) return true;
    const variants = new Map();
    for (const row of rows) {
      const selector = row.querySelector('cart-quantity-selector-component');
      const input = selector?.querySelector('input[ref="quantityInput"], input[name="updates[]"]');
      if (!(selector instanceof HTMLElement) || !(input instanceof HTMLInputElement)) continue;
      const variantId = String(selector.dataset.variantId || '');
      if (!variantId) continue;
      const data = variants.get(variantId) || {
        normalQuantity: 0,
        availableInventory: toInt(selector.dataset.vitaminatAvailableInventory, 0),
        preorderEligible: isTrue(selector.dataset.vitaminatPreorderEligible),
        inventoryManaged: isTrue(selector.dataset.vitaminatInventoryManaged),
      };
      data.availableInventory = Math.max(data.availableInventory, toInt(selector.dataset.vitaminatAvailableInventory, 0));
      data.preorderEligible = data.preorderEligible || isTrue(selector.dataset.vitaminatPreorderEligible);
      data.inventoryManaged = data.inventoryManaged || isTrue(selector.dataset.vitaminatInventoryManaged);
      if (!this.#isPreorderLine(row)) data.normalQuantity += toInt(input.value, 0);
      variants.set(variantId, data);
    }
    for (const data of variants.values()) {
      if (!data.preorderEligible || !data.inventoryManaged) continue;
      if (data.normalQuantity > data.availableInventory) return false;
    }
    return true;
  }

  #showCheckoutGuardMessage(messageText = CHECKOUT_GUARD_MESSAGE) {
    const messages = document.querySelectorAll('[data-vitaminat-cart-checkout-guard-message]');
    for (const message of messages) {
      if (!(message instanceof HTMLElement)) continue;
      message.textContent = messageText;
      message.hidden = false;
      message.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  #hideCheckoutGuardMessage() {
    document.querySelectorAll('[data-vitaminat-cart-checkout-guard-message]').forEach((message) => {
      if (message instanceof HTMLElement) message.hidden = true;
    });
  }

  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;
    if (quantity === 0) return this.onLineItemRemove(line);
    const lineItemRow = this.refs.cartItemRows[line - 1];
    const quantitySelector = event.target instanceof Element ? event.target.closest('cart-quantity-selector-component') : null;
    const splitConfig = this.#getCartSplitConfig(lineItemRow, quantitySelector);
    if (splitConfig.shouldSplit && quantity > splitConfig.availableInventory) {
      if (splitConfig.inventoryPolicy !== 'continue' && !splitConfig.bundleHasPreorder) {
        this.#handleCartError(line, { errors: INVENTORY_POLICY_DENY_MESSAGE });
        return;
      }
      const normalQuantity = Math.max(splitConfig.availableInventory, 0);
      const preorderQuantity = quantity - normalQuantity;
      this.updateQuantity({
        line,
        quantity: normalQuantity,
        action: 'change',
        preorderQuantity,
        preorderProperties: {
          eta: splitConfig.eta,
          delayPolicy: splitConfig.delayPolicy,
          variantId: splitConfig.variantId,
        },
      });
    } else {
      this.updateQuantity({ line, quantity, action: 'change' });
    }
    if (!lineItemRow) return;
    const textComponent = lineItemRow.querySelector('text-component');
    textComponent?.shimmer?.();
  }

  #getCartSplitConfig(lineItemRow, quantitySelector) {
    const dataset = quantitySelector instanceof HTMLElement ? quantitySelector.dataset : {};
    const isPreorderLine = this.#isPreorderLine(lineItemRow);
    const bundleHasPreorder = isTrue(dataset.vitaminatBundleHasPreorder);
    const preorderEligible = isTrue(dataset.vitaminatPreorderEligible) || bundleHasPreorder;
    const forcePreorder = isTrue(dataset.vitaminatForcePreorder);
    const inventoryManaged = isTrue(dataset.vitaminatInventoryManaged) || bundleHasPreorder;
    const availableInventory = toInt(dataset.vitaminatAvailableInventory, 0);
    const inventoryPolicy = String(dataset.vitaminatInventoryPolicy || '');
    return {
      shouldSplit: !isPreorderLine && preorderEligible && !forcePreorder && inventoryManaged,
      variantId: String(dataset.variantId || ''),
      availableInventory,
      inventoryPolicy,
      eta: String(dataset.vitaminatPreorderEta || ''),
      delayPolicy: String(dataset.vitaminatDelayPolicy || DEFAULT_DELAY_POLICY),
      bundleHasPreorder,
    };
  }

  #isPreorderLine(lineItemRow) {
    if (!lineItemRow) return false;
    const properties = lineItemRow.querySelectorAll('.cart-items__properties');
    for (const property of properties) {
      const label = property.querySelector('dt')?.textContent?.trim().replace(/:$/, '');
      const value = property.querySelector('dd')?.textContent?.trim();
      if (label === PREORDER_TYPE_PROPERTY && value === PREORDER_TYPE_VALUE) return true;
    }
    return false;
  }

  onLineItemRemove(line) {
    this.updateQuantity({ line, quantity: 0, action: 'clear' });
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;
    const rowsToRemove = [cartItemRowToRemove, ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key)];
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();
      if (prefersReducedMotion()) return remove();
      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    this.#disableCartItems();
    this.#hideCheckoutGuardMessage();
    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const sectionsToUpdate = this.#getSectionsToUpdate();
    const body = JSON.stringify({ line, quantity, sections: Array.from(sectionsToUpdate).join(','), sections_url: window.location.pathname });
    cartTotal?.shimmer();
    try {
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const parsedResponseText = JSON.parse(await response.text());
      resetShimmer(this);
      if (parsedResponseText.errors) {
        this.#handleCartError(line, parsedResponseText);
        return false;
      }
      let finalCartResponse = parsedResponseText;
      if (config.preorderQuantity > 0 && config.preorderProperties?.variantId) {
        const preorderResponse = await this.#addPreorderQuantity(config.preorderProperties.variantId, config.preorderQuantity, config.preorderProperties, sectionsToUpdate);
        if (preorderResponse.errors || preorderResponse.status) {
          const message = preorderResponse.description || preorderResponse.message || preorderResponse.errors || CHECKOUT_GUARD_MESSAGE;
          this.#handleCartError(line, { errors: message });
          return false;
        }
        finalCartResponse = preorderResponse;
      }
      this.#renderCartResponse(finalCartResponse);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  async #addPreorderQuantity(variantId, quantity, preorderProperties, sectionsToUpdate) {
    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', String(quantity));
    formData.set(`properties[${PREORDER_TYPE_PROPERTY}]`, PREORDER_TYPE_VALUE);
    if (preorderProperties.eta) formData.set(`properties[${PREORDER_ETA_PROPERTY}]`, preorderProperties.eta);
    if (preorderProperties.delayPolicy) formData.set(`properties[${PREORDER_POLICY_PROPERTY}]`, preorderProperties.delayPolicy);
    formData.set('sections', Array.from(sectionsToUpdate).join(','));
    formData.set('sections_url', window.location.pathname);
    const fetchCfg = fetchConfig('javascript', { body: formData });
    const response = await fetch(Theme.routes.cart_add_url, { ...fetchCfg, headers: { ...fetchCfg.headers, Accept: 'text/html' } });
    return response.json();
  }

  #getSectionsToUpdate() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionsToUpdate.add(item.dataset.sectionId);
    });
    return sectionsToUpdate;
  }

  #renderCartResponse(parsedResponseText) {
    const newSectionHTML = new DOMParser().parseFromString(parsedResponseText.sections[this.sectionId], 'text/html');
    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;
    this.#updateQuantitySelectors(parsedResponseText);
    this.dispatchEvent(new CartUpdateEvent(parsedResponseText, this.sectionId, { itemCount: newCartItemCount, source: 'cart-items-component', sections: parsedResponseText.sections }));
    morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
    this.#updateCartQuantitySelectorButtonStates();
  }

  handleDiscountUpdate = (event) => { this.#handleCartUpdate(event); };

  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    if (!quantityInput) throw new Error('Quantity input not found');
    quantityInput.value = quantityInput.defaultValue;
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];
    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');
    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;
    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  #disableCartItems() { this.classList.add('cart-items-disabled'); }
  #enableCartItems() { this.classList.remove('cart-items-disabled'); }

  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;
    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`);
      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;
        input.setAttribute('data-cart-quantity', item.quantity.toString());
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') selector.updateCartQuantity();
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) selector.updateButtonStates?.();
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
